import { Portal } from "@mantine/core";
import { useMemo } from "react";
import { Outlet, useLocation } from "react-router-dom";

import { useLoading } from "@app/common/hooks/useLoading";
import { useGetCategories } from "@app/services/category";
import { useAuthentication } from "@app/store/useAuthtentication";

import AppLoader from "@app/common/components/AppLoader";
import Header from "@app/common/components/Header";
import Sidebar from "@app/common/components/Sidebar";

import { AllCategory, CategoriesResponse } from "@app/services/category/types";
import { useGenericData } from "@app/store/useGenericData";
import SiteSelection from "./menu/SiteSelection";
import "./styles.scss";
import { useGetAccounts } from "@app/services/account";
import { useIsomorphicEffect } from "@mantine/hooks";

export default function Features() {
  const isLoading = useLoading();
  const location = useLocation();
  const { genericData, setGenericData } = useGenericData((state) => state);
  const { idProfile, idModule, idUser, idAccount } = useAuthentication(
    (state) => state.auth.dataBiowel
  );
  const { createAuthentication, auth } = useAuthentication();

  const { setCategories, setAllCategories } = useAuthentication((state) => state);

  const { data: accountsData } = useGetAccounts(idUser);

  const categoriesQuery = useGetCategories({
    id_profile: idProfile
  });

  const categoryModule = [
    categoriesQuery.data?.all_Categories.find((mod) => mod.id === idModule)
  ] as AllCategory[];

  const applyClass = () => {
    const excludedPaths = [
      "/dispensation/drugDelivery",
      "/dispensation/drugDelivery/dispensary/detail",
      "/dispensation/drugDelivery/eps/order",
      "/dispensation/drugDelivery/patientOrderInformation",
      "/dispensation/drugDelivery/patientOrderInformation",
      "/dispensation/drugDelivery/dispensary/detailOrder",
      "/dispensation/deliveryAuth/detail",
      "/dispensation/supportParameters/create",
      "/dispensation/supportParameters/edit",
      "/dispensation/store"
    ];

    const isDynamicRoute = location.pathname.match(
      /^\/dispensation\/drugDelivery\/patientOrderInformation\/[^/]+\/edit$/
    );

    return !excludedPaths.some((path) => location.pathname === path) && !isDynamicRoute;
  };

  const accounts = useMemo(() => {
    return accountsData?.accounts ?? [];
  }, [accountsData]);

  useIsomorphicEffect(() => {
    const selectedAccount = accounts.find((account) => account.id === +idAccount);

    createAuthentication({
      ...auth,
      dataBiowel: { ...auth.dataBiowel, accounts, dataPolicy: selectedAccount?.data_policy ?? "" }
    });

    if (selectedAccount) {
      setGenericData({ ...genericData, accountName: selectedAccount?.name ?? "" });
    }
  }, [accounts, idAccount]);

  useIsomorphicEffect(() => {
    if (categoriesQuery.data) {
      if (categoryModule) {
        setCategories(categoryModule);
      }
      setAllCategories(categoriesQuery.data as CategoriesResponse);
    }
  }, [categoriesQuery.data]);

  const renderComponent = () => {
    return (
      <>
        {isLoading && (
          <Portal target="#app-loader">
            <AppLoader />
          </Portal>
        )}
        <div className="app-container">
          <Header />

          {genericData.siteId ? (
            <div className="app-viewport">
              <Sidebar />
              <main
                id="mainContainer"
                className={`${applyClass() ? "main-container" : "main-container-noP"}`}
              >
                <Outlet />
              </main>
            </div>
          ) : (
            <div className="app-viewport">
              <main
                id="mainContainer"
                className={`${applyClass() ? "main-container" : "main-container-noP"}`}
              >
                <SiteSelection />
              </main>
            </div>
          )}
        </div>
      </>
    );
  };

  return renderComponent();
}
